import type { Control } from "../core/Control.js";
import type { Desktop } from "../core/Desktop.js";
import { MarkupError, type MarkupPosition } from "./errors.js";

/**
 * What a variable holds.
 *
 * `color` is kept as the text it was written as - a palette name or a hex string - because the
 * controls it is linked to take color references and resolve them against the theme themselves.
 * `choice` holds one of a list, and with `cycle` it takes turns through them on its own.
 */
export type VariableType = "number" | "text" | "boolean" | "color" | "choice";

export const VARIABLE_TYPES: readonly VariableType[] = ["number", "text", "boolean", "color", "choice"];

export type VariableValue = number | string | boolean;

export interface VariableDefinition {
  name: string;
  type: VariableType;
  value: VariableValue;
  /**
   * Numbers only: how quickly the shown value closes on a new one, as a rate a second - the
   * fraction of the remaining distance covered is `min(1, dt * ease)`. 0 jumps.
   */
  ease?: number;
  /** Choices only: seconds each one is shown before the next. 0 holds the first. */
  cycle?: number;
  choices?: readonly string[];
}

interface LinkSpec {
  control: Control;
  prop: string;
  /** `#` stands for the value; without a format the value is assigned as it is. */
  format: string | null;
  decimals: number;
  at: MarkupPosition;
}

interface Slot {
  def: VariableDefinition;
  /** What was last set, and what the links are heading toward. */
  target: VariableValue;
  /** What the links are showing now: the target, or an eased number on its way there. */
  shown: VariableValue;
  cycleStart: number;
}

/**
 * The live half of a document: named values a host sets, and the control properties linked to
 * them.
 *
 * This is the whole of the host feed. The game's loading screen used to be a class that eased its
 * own bar, counted its own seconds to rotate tips and repainted two layers by hand; with this it
 * is `view.set("progress", 64)` and the markup says what follows the number. It is deliberately
 * one-way - a value flows *into* the controls - so there is nothing to keep in sync in the other
 * direction and nothing that can loop.
 */
export class VariableStore {
  #slots = new Map<string, Slot>();
  #links = new Map<string, LinkSpec[]>();
  #pending: { name: string; link: LinkSpec }[] = [];
  #lastNow = -1;
  #stop: (() => void) | null = null;
  #listeners = new Set<(name: string) => void>();

  /**
   * Hear every change to what a variable is showing - for a reader that is not a linked property,
   * such as a drawing whose expressions name variables. Returns the way to stop listening.
   */
  onChange(listener: (name: string) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  define(def: VariableDefinition, at: MarkupPosition): void {
    if (this.#slots.has(def.name)) {
      throw new MarkupError(`a variable named "${def.name}" is already declared`, at);
    }
    const value = def.type === "choice" && def.choices?.length && def.value === "" ? def.choices[0]! : def.value;
    this.#slots.set(def.name, { def, target: value, shown: value, cycleStart: -1 });
  }

  /** Declared names, in declaration order. */
  names(): string[] {
    return [...this.#slots.keys()];
  }

  has(name: string): boolean {
    return this.#slots.has(name);
  }

  /** What was last set: the target, not the eased value on its way there. */
  get(name: string): VariableValue | undefined {
    return this.#slots.get(name)?.target;
  }

  /** What the links are showing now. */
  shown(name: string): VariableValue | undefined {
    return this.#slots.get(name)?.shown;
  }

  /**
   * Set a variable. A number that eases heads toward the new value from wherever it is, unless
   * `immediate` - a bar put back to empty for the next load should not be seen draining - and
   * anything else reaches its links at once. Setting an undeclared name is an error, because it is
   * the host's typo and a silent one would look like a screen that does not update.
   */
  set(name: string, value: VariableValue, immediate = false): void {
    const slot = this.#slots.get(name);
    if (!slot) {
      throw new MarkupError(
        `no variable is named "${name}"; this document declares ${this.names().join(", ") || "none"}`,
        { line: 0, col: 0 },
      );
    }
    slot.target = coerce(slot.def.type, value);
    if (!immediate && slot.def.type === "number" && (slot.def.ease ?? 0) > 0) return;
    slot.shown = slot.target;
    if (slot.def.type === "choice" && slot.def.cycle) slot.cycleStart = -1;
    this.#apply(name);
  }

  /** Link `control[prop]` to a variable. Checked against the declarations by `resolve`. */
  link(name: string, control: Control, prop: string, format: string | null, decimals: number, at: MarkupPosition): void {
    this.#pending.push({ name, link: { control, prop, format, decimals, at } });
  }

  /**
   * Check every link names a declared variable, then give each control its starting value.
   * Called once the whole document is built, so a `<Variables>` block may come after a link.
   */
  resolve(): void {
    for (const { name, link } of this.#pending) {
      if (!this.#slots.has(name)) {
        throw new MarkupError(
          `<Link> names a variable "${name}" nobody declared; declared: ${this.names().join(", ") || "none"}`,
          link.at,
        );
      }
      const list = this.#links.get(name) ?? [];
      list.push(link);
      this.#links.set(name, list);
    }
    this.#pending.length = 0;
    for (const name of this.#links.keys()) this.#apply(name);
  }

  /** Start advancing eased numbers and cycling choices with the desktop's clock. */
  attach(desktop: Desktop): void {
    this.#stop?.();
    this.#stop = this.#slots.size > 0 ? desktop.onFrame((now) => this.step(now)) : null;
  }

  detach(): void {
    this.#stop?.();
    this.#stop = null;
  }

  /**
   * Advance to `now`, in seconds. Returns true while anything is still moving.
   * @internal Run by the desktop before each frame; exposed for tests.
   */
  step(now: number): boolean {
    const dt = this.#lastNow < 0 ? 0 : Math.min(0.1, Math.max(0, now - this.#lastNow));
    this.#lastNow = now;
    let moving = false;
    for (const [name, slot] of this.#slots) {
      const { def } = slot;
      if (def.type === "number" && (def.ease ?? 0) > 0 && slot.shown !== slot.target) {
        const from = slot.shown as number;
        const to = slot.target as number;
        const next = from + (to - from) * Math.min(1, dt * def.ease!);
        slot.shown = Math.abs(to - next) < 1e-3 ? to : next;
        this.#apply(name);
        if (slot.shown !== to) moving = true;
      }
      if (def.type === "choice" && (def.cycle ?? 0) > 0 && def.choices && def.choices.length > 1) {
        if (slot.cycleStart < 0) slot.cycleStart = now - def.choices.indexOf(String(slot.target)) * def.cycle!;
        const index = Math.floor((now - slot.cycleStart) / def.cycle!) % def.choices.length;
        const choice = def.choices[index]!;
        if (choice !== slot.shown) {
          slot.shown = choice;
          slot.target = choice;
          this.#apply(name);
        }
        moving = true;
      }
    }
    return moving;
  }

  #apply(name: string): void {
    for (const listener of this.#listeners) listener(name);
    const slot = this.#slots.get(name);
    const links = this.#links.get(name);
    if (!slot || !links) return;
    for (const link of links) {
      const value = present(slot.shown, link);
      const target = link.control as unknown as Record<string, unknown>;
      if (target[link.prop] !== value) {
        target[link.prop] = value;
        link.control.invalidate();
      }
    }
  }
}

function present(value: VariableValue, link: LinkSpec): VariableValue {
  const shown = typeof value === "number" && link.decimals >= 0 ? round(value, link.decimals) : value;
  return link.format === null ? shown : link.format.replace(/#/g, String(shown));
}

function round(value: number, decimals: number): number {
  if (decimals < 0) return value;
  const k = 10 ** decimals;
  return Math.round(value * k) / k;
}

function coerce(type: VariableType, value: VariableValue): VariableValue {
  switch (type) {
    case "number": {
      const n = typeof value === "number" ? value : Number(value);
      return Number.isFinite(n) ? n : 0;
    }
    case "boolean":
      return value === true || value === "true";
    default:
      return String(value);
  }
}

/** Read a variable's written value as its type. */
export function parseVariableValue(type: VariableType, text: string): VariableValue {
  return coerce(type, type === "number" ? Number(text.trim() || "0") : text);
}
